import React, { useState } from 'react';
import { UserPlus, UserMinus, Users, ChevronDown, ChevronRight } from 'lucide-react';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  director: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  manager: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  associate: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  user: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

function ReportRow({ report, onRemoveReport, supervisorId, theme, depth = 0 }) {
  const [expanded, setExpanded] = useState(true);
  const hasSubReports = report.direct_reports?.length > 0;

  return (
    <>
      <div
        className={`flex items-center justify-between p-4 ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${16 + depth * 32}px` }}
      >
        <div className="flex items-center gap-3">
          {/* Expand toggle for nested reports */}
          {hasSubReports ? (
            <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-gray-400">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <div className="w-[18px]" />
          )}

          {report.avatar_url ? (
            <img src={report.avatar_url} alt="" className="w-9 h-9 rounded-full" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
              {(report.first_name || report.username)?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-sm font-rasa font-medium">
              {report.first_name ? `${report.first_name} ${report.last_name || ''}`.trim() : report.username}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ROLE_COLORS[report.role] || ROLE_COLORS.user}`}>
                {report.role_display || report.role}
              </span>
              {(report.departments || []).map((d, i) => (
                <span
                  key={i}
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-rasa"
                  title={d.service_area_name ? `${d.department_name} · ${d.service_area_name}` : d.department_name}
                >
                  {d.department_name}{d.service_area_name ? ` · ${d.service_area_name}` : ''}
                </span>
              ))}
              {hasSubReports && (
                <span className="text-[10px] text-gray-400 font-rasa">
                  {report.direct_reports.length} report{report.direct_reports.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        {onRemoveReport && (
          <button
            onClick={() => onRemoveReport(report.id, supervisorId)}
            className="p-2 rounded-lg text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Remove from reports"
          >
            <UserMinus size={16} />
          </button>
        )}
      </div>

      {/* Nested reports */}
      {expanded && hasSubReports && report.direct_reports.map(sub => (
        <ReportRow
          key={sub.id}
          report={sub}
          onRemoveReport={onRemoveReport}
          supervisorId={report.id}
          theme={theme}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

export default function MyTeamPanel({ hierarchy, currentUser, onAssignReport, onRemoveReport, assignableUsers, isDirectorOrAbove, theme }) {
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Find current user in hierarchy
  const me = hierarchy.find(u => u.id === currentUser?.id);
  const directReports = me?.direct_reports || [];

  const handleAdd = () => {
    if (selectedUserId) {
      onAssignReport(selectedUserId, currentUser?.id);
      setSelectedUserId('');
      setShowAddUser(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} />
          <h2 className="font-dm-serif font-semibold">My Direct Reports</h2>
          <span className={`text-sm font-rasa px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
            {directReports.length}
          </span>
        </div>
        {isDirectorOrAbove || currentUser?.role === 'manager' ? (
          <button
            onClick={() => setShowAddUser(!showAddUser)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-rasa"
          >
            <UserPlus size={14} />
            Add Report
          </button>
        ) : null}
      </div>

      {/* Add User Form — only for managers and above */}
      {showAddUser && (isDirectorOrAbove || currentUser?.role === 'manager') && (
        <div className={`flex items-center gap-3 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className={`flex-1 px-3 py-2 rounded-lg border text-sm font-rasa ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          >
            <option value="">Select a user...</option>
            {assignableUsers
              .filter(u => u.id !== currentUser?.id && !directReports.some(dr => dr.id === u.id))
              .map(u => (
                <option key={u.id} value={u.id}>
                  {u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.username} ({u.role_display || u.role})
                </option>
              ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={!selectedUserId}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-rasa"
          >
            Add
          </button>
          <button
            onClick={() => setShowAddUser(false)}
            className={`px-4 py-2 rounded-lg text-sm font-rasa ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Reports List */}
      {directReports.length === 0 ? (
        <div className={`text-center py-12 rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <Users size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-rasa text-gray-500">No direct reports yet</p>
          <p className="text-xs font-rasa text-gray-400 mt-1">Click "Add Report" to add someone to your team</p>
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {directReports.map(report => (
            <ReportRow
              key={report.id}
              report={report}
              onRemoveReport={onRemoveReport}
              supervisorId={currentUser?.id}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
}
